/**
 * TeamListCard.tsx
 *
 * Renders the `team_list` envelope data emitted by onething_list_teams.
 */

import { Building2, Inbox } from "lucide-react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";

// ---------------------------------------------------------------------------
// Data shape emitted by the BE envelope for kind "team_list"
// ---------------------------------------------------------------------------

export interface TeamRow {
  pid: string;
  name: string;
  description?: string;
}

// ---------------------------------------------------------------------------
// Single row
// ---------------------------------------------------------------------------

function TeamRowItem({ row }: { row: TeamRow }) {
  return (
    <div className="mx-1 rounded-lg px-2 py-2.5">
      <p className="text-[13px] font-semibold tracking-tight leading-tight">{row.name}</p>
      {row.description && (
        <p className="text-[11px] text-muted-foreground mt-0.5">{row.description}</p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Card
// ---------------------------------------------------------------------------

interface TeamListCardProps {
  summary: string;
  rows: TeamRow[];
}

export function TeamListCard({ summary, rows }: TeamListCardProps) {
  return (
    <Card className="my-2 rounded-xl border-border/60 shadow-sm dark:shadow-none dark:ring-1 dark:ring-border/40 overflow-hidden">
      <CardHeader className="flex flex-row items-start gap-2.5 px-4 py-3">
        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <Building2 className="h-3.5 w-3.5" />
        </div>
        <p className="text-[13.5px] font-bold leading-tight tracking-tight flex-1 min-w-0">
          {summary}
        </p>
        <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-[10.5px] font-semibold text-muted-foreground">
          {rows.length}
        </span>
      </CardHeader>
      <CardContent className="px-2 pb-2 pt-0">
        {rows.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 py-8 text-center text-muted-foreground">
            <Inbox className="h-6 w-6 opacity-50" />
            <span className="text-xs">No teams found.</span>
          </div>
        ) : (
          <div>
            {rows.map((row) => (
              <TeamRowItem key={row.pid} row={row} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
